/*****************************************************************
 *  ZOHO BRIDGE PANEL — SMALL SHARED PIECES                        *
 *  File: apps/web/src/pages/bridge/bridgeParts.js                 *
 *  What it does: the little building blocks every tab uses:       *
 *  a date in plain words, a status chip (word + icon + colour),   *
 *  a "why" box with confirm/cancel, and a loading/empty/error     *
 *  message. Each is written once and used everywhere.             *
 *  Reads: nothing.   Writes: nothing.                             *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 — DATES IN PLAIN WORDS                               *
 *  SECTION 2 — THE STATUS CHIP                                    *
 *  SECTION 3 — THE WHY BOX                                        *
 *  SECTION 4 — LOADING / EMPTY / ERROR                            *
 *****************************************************************/

import React, { useState } from 'react';
import { BridgeIcon } from './bridgeIcons';
import { BRIDGE_WORDS, STATUS_NAMES } from './bridgeWords';

/*****************************************************************
 *  SECTION 1 — DATES IN PLAIN WORDS                               *
 *****************************************************************/

/* ---- formatWhen(isoDate) ---------------------------------------
   "Oct 7, 2026, 6:15 AM" in the viewer's own time zone.
   In: an ISO date (or null).  Out: text ("" when missing).      */
export function formatWhen(isoDate) {
  if (!isoDate) {
    return '';
  }

  const asDate = new Date(isoDate);

  if (Number.isNaN(asDate.getTime())) {
    return '';
  }

  return asDate.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/* ---- formatMoney(rate) -- "$205.00" ---- */
export function formatMoney(rate) {
  const asNumber = Number(rate);

  if (!Number.isFinite(asNumber)) {
    return '';
  }

  return '$' + asNumber.toFixed(2);
}

/*****************************************************************
 *  SECTION 2 — THE STATUS CHIP                                    *
 *  Colour never stands alone: every chip has a word and an icon.  *
 *****************************************************************/

// Which look and icon each status wears.
const CHIP_LOOKS = {
  proposed: { tone: 'info', icon: 'clock' },
  approved: { tone: 'info', icon: 'clock' },
  applied: { tone: 'ok', icon: 'check' },
  rejected: { tone: 'neutral', icon: 'cross' },
  failed: { tone: 'bad', icon: 'alert' },
};

/* ---- StatusChip({ status }) -- one status as a chip ---- */
export function StatusChip({ status }) {
  const look = CHIP_LOOKS[status] || { tone: 'neutral', icon: 'list' };
  const word = STATUS_NAMES[status] || status;

  return (
    <span className={'zb-chip zb-chip-' + look.tone}>
      <BridgeIcon name={look.icon} size={16} />
      <span>{word}</span>
    </span>
  );
}

/* ---- ToneChip({ tone, icon, word }) -- any chip ---- */
export function ToneChip({ tone, icon, word }) {
  return (
    <span className={'zb-chip zb-chip-' + tone}>
      <BridgeIcon name={icon} size={16} />
      <span>{word}</span>
    </span>
  );
}

/*****************************************************************
 *  SECTION 3 — THE WHY BOX                                        *
 *****************************************************************/

/* ---- WhyBox({ confirmWord, tone, onConfirm, onCancel, busy,
                 extra }) -------------------------------------
   A text box for the reason, plus Confirm and Cancel.
   Confirm stays off until at least two characters are typed
   (the server asks the same).
   onConfirm(whyText) is called with the trimmed reason.        */
export function WhyBox({ confirmWord, tone, onConfirm, onCancel, busy, extra, label }) {
  const [whyText, setWhyText] = useState('');
  const MIN_WHY_LENGTH = 2; // same rule as the server
  const trimmedWhy = whyText.trim();
  const isLongEnough = trimmedWhy.length >= MIN_WHY_LENGTH;
  const [boxId] = useState(() => 'zb-why-' + Math.random().toString(36).slice(2, 8)); // stable label link

  return (
    <div className="zb-whybox">
      <label htmlFor={boxId} className="zb-label">{label || BRIDGE_WORDS.whyLabel}</label>
      <textarea
        id={boxId}
        className="zb-textarea"
        rows={2}
        value={whyText}
        placeholder={BRIDGE_WORDS.whyPlaceholder}
        onChange={(event) => setWhyText(event.target.value)}
      />
      {extra}
      <div className="zb-row">
        <button
          type="button"
          className={'zb-button ' + (tone === 'danger' ? 'zb-button-danger' : 'zb-button-primary')}
          disabled={!isLongEnough || busy}
          onClick={() => onConfirm(trimmedWhy)}
        >
          {busy ? BRIDGE_WORDS.loading : confirmWord}
        </button>
        <button type="button" className="zb-button zb-button-plain" onClick={onCancel} disabled={busy}>
          {BRIDGE_WORDS.cancel}
        </button>
      </div>
    </div>
  );
}

/*****************************************************************
 *  SECTION 4 — LOADING / EMPTY / ERROR                            *
 *****************************************************************/

/* ---- LoadState({ state, emptyWord, errorWord, onRetry }) -------
   Shows "Loading…", an empty message, or an error with Try again.
   state is 'loading' | 'empty' | 'error'.                      */
export function LoadState({ state, emptyWord, errorWord, onRetry }) {
  if (state === 'loading') {
    return <p className="zb-quiet" role="status">{BRIDGE_WORDS.loading}</p>;
  }

  if (state === 'error') {
    return (
      <div className="zb-notice zb-notice-bad" role="alert">
        <BridgeIcon name="alert" />
        <span>{errorWord || BRIDGE_WORDS.loadFailed}</span>
        {onRetry ? (
          <button type="button" className="zb-button zb-button-plain" onClick={onRetry}>
            {BRIDGE_WORDS.tryAgain}
          </button>
        ) : null}
      </div>
    );
  }

  return <p className="zb-quiet">{emptyWord}</p>;
}
